import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, Eye, Users, UserCircle, Buildings, Warning, CheckCircle } from "@phosphor-icons/react";

const AUDIENCES = [
  { key: "contractors", label: "Contractors", icon: Users, hint: "Every active worker with an email on file." },
  { key: "supervisors", label: "Client Supervisors", icon: UserCircle, hint: "Distinct supervisor emails across active projects." },
  { key: "billing_contacts", label: "Client Billing Contacts", icon: Buildings, hint: "Distinct billing emails across active clients." },
];

const DEFAULT_BODY = `<p>Hi {{name}},</p>

<p>[Write your message here. Use {{name}} anywhere in the body — it will be replaced with each recipient's first name automatically.]</p>

<p>Thanks,<br/>Bo Harte<br/>Safety Onsite Solutions</p>`;

export default function Announcements() {
  const [audience, setAudience] = useState("contractors");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState(DEFAULT_BODY);
  const [recipients, setRecipients] = useState(null);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    setRecipients(null);
    setLastResult(null);
    api.get("/tools/announcements/recipients", { params: { audience } })
      .then((r) => setRecipients(r.data.recipients))
      .catch(() => setRecipients([]));
  }, [audience]);

  const sendIt = async () => {
    if (!subject.trim()) return toast.error("Subject is required.");
    if (!bodyHtml.trim()) return toast.error("Message body is required.");
    if (!recipients || recipients.length === 0) return toast.error("No recipients for this audience.");
    if (!window.confirm(`Send this to ${recipients.length} ${audience}? This action can't be undone.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/tools/announcements/send", {
        audience, subject, body_html: bodyHtml,
      });
      setLastResult(data);
      toast.success(`Sent to ${data.sent} recipient${data.sent === 1 ? "" : "s"}.` + (data.failed ? ` ${data.failed} failed.` : ""));
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <div className="tinylabel">Communications</div>
        <h1 className="text-3xl font-black flex items-center gap-2">
          <PaperPlaneTilt size={30} weight="bold" className="text-teal-600" />
          Announcement Sender
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          One-shot bulk email to your workers, client supervisors, or billing contacts. Every send is logged in the audit trail.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 mb-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">1. Who receives this?</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {AUDIENCES.map((a) => {
            const Icon = a.icon;
            const active = audience === a.key;
            return (
              <button key={a.key} onClick={() => setAudience(a.key)}
                      data-testid={`audience-${a.key}`}
                      className={`p-3 rounded-md border-2 text-left transition ${active ? "border-teal-500 bg-teal-50" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="flex items-center gap-2">
                  <Icon size={20} weight="bold" className={active ? "text-teal-700" : "text-slate-500"} />
                  <div className="font-bold">{a.label}</div>
                </div>
                <div className="text-xs text-slate-500 mt-1">{a.hint}</div>
              </button>
            );
          })}
        </div>
        <div className="mt-3 text-sm">
          {recipients === null ? <span className="text-slate-400">Loading recipients…</span>
             : recipients.length === 0 ? <span className="text-rose-600 flex items-center gap-1"><Warning size={14} weight="fill" /> No recipients found — none in that audience have a valid email.</span>
             : <span className="text-slate-700"><b>{recipients.length}</b> recipient{recipients.length === 1 ? "" : "s"} will receive this email.</span>}
        </div>
        {recipients && recipients.length > 0 && (
          <details className="mt-2">
            <summary className="text-xs text-teal-700 cursor-pointer font-semibold" data-testid="show-recipients">Show recipient list</summary>
            <ul className="mt-2 max-h-40 overflow-y-auto bg-slate-50 rounded p-2 text-xs font-mono space-y-0.5">
              {recipients.map((r, i) => (
                <li key={i}>• {r.name || "(no name)"} — {r.email}</li>
              ))}
            </ul>
          </details>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 mb-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">2. Compose</div>
        <label className="text-xs font-semibold text-slate-600">Subject line</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)}
               data-testid="announcement-subject"
               className="w-full h-11 px-3 mt-1 mb-4 border border-slate-300 rounded-md"
               placeholder="e.g. Weekly timesheets are moving online starting Feb 16" />
        <label className="text-xs font-semibold text-slate-600">Body (HTML — use <code className="bg-slate-100 px-1 rounded">{"{{name}}"}</code> to insert each recipient's first name)</label>
        <textarea value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)}
                  data-testid="announcement-body"
                  rows={12}
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md font-mono text-sm" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 mb-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">3. Preview (with a sample recipient)</div>
        <div className="border border-slate-200 rounded-md bg-slate-50 p-4 text-sm">
          <div className="mb-2 pb-2 border-b border-slate-200">
            <div className="text-xs text-slate-500">Subject:</div>
            <div className="font-semibold">{subject || "(no subject)"}</div>
          </div>
          <div dangerouslySetInnerHTML={{ __html: (bodyHtml || "<em>(empty body)</em>").replace(/\{\{name\}\}/g, "Sample") }} />
        </div>
      </div>

      <button onClick={sendIt} disabled={sending}
              data-testid="send-announcement-btn"
              className="w-full h-14 bg-green-600 hover:bg-green-700 disabled:bg-slate-400 text-white text-lg font-bold rounded-lg flex items-center justify-center gap-2">
        {sending ? <>Sending…</> : <><PaperPlaneTilt size={22} weight="bold" /> Send to {recipients?.length || 0} recipient{recipients?.length === 1 ? "" : "s"}</>}
      </button>

      {lastResult && (
        <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-md">
          <div className="font-bold flex items-center gap-2 text-emerald-800">
            <CheckCircle size={18} weight="fill" /> Sent
          </div>
          <div className="text-sm text-emerald-900 mt-1">
            {lastResult.sent} delivered · {lastResult.failed} failed
          </div>
          {lastResult.failed > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-rose-700">See failed recipients</summary>
              <ul className="mt-1 font-mono">
                {(lastResult.failed_list || []).map((f, i) => (
                  <li key={i}>{f.email}: {f.reason}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
